import React, { useState } from 'react';
import { X, Mail, Send, ArrowLeft, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';
import Button from './ui/Button';
import Modal from './ui/Modal';

const ForgotPasswordModal = ({ isOpen, onClose }) => {
  const [step, setStep] = useState('request'); // 'request', 'sent', 'reset'
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

  const handleRequestReset = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setError('Email address is required');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API_BASE_URL}/api/auth/forgot-password`, {
        email: email
      });

      setSuccess('Password reset instructions sent to your email!');
      setStep('sent');
      
    } catch (error) {
      console.error('Forgot password error:', error);
      setError(error.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!resetToken) {
      setError('Reset token is required');
      return;
    }
    if (!newPassword) {
      setError('New password is required');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.post(`${API_BASE_URL}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });

      setSuccess('Password reset successful! You can now login with your new password.');
      
      // Close modal after 3 seconds
      setTimeout(() => {
        handleClose();
      }, 3000);
      
    } catch (error) {
      console.error('Reset password error:', error);
      setError(error.response?.data?.detail || 'Failed to reset password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep('request');
    setEmail('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
    setError('');
    setSuccess('');
    onClose();
  };

  const goToResetStep = () => {
    setStep('reset');
    setError('');
    setSuccess('');
  };

  const goBackToRequest = () => {
    setStep('request');
    setError('');
    setSuccess('');
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} maxWidth="500px">
      <div style={{
        padding: '24px',
        maxWidth: '500px',
        width: '100%'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '24px',
          paddingBottom: '16px',
          borderBottom: '1px solid var(--color-border)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: 'var(--gradient-warning)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <Mail size={20} color="white" />
            </div>
            <div>
              <h2 style={{
                fontSize: '18px',
                fontWeight: '600',
                color: 'var(--color-text-primary)',
                margin: 0
              }}>
                {step === 'request' ? 'Forgot Password' : step === 'sent' ? 'Check Your Email' : 'Reset Password'}
              </h2>
              <p style={{
                fontSize: '12px',
                color: 'var(--color-text-muted)',
                margin: 0
              }}>
                {step === 'request' 
                  ? 'Enter your email to receive reset instructions' 
                  : step === 'sent'
                  ? 'We\'ve sent you a reset link'
                  : 'Enter your new password'
                }
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              borderRadius: '4px',
              color: 'var(--color-text-muted)',
              transition: 'color 0.15s ease'
            }}
            onMouseEnter={(e) => e.target.style.color = 'var(--color-text-secondary)'}
            onMouseLeave={(e) => e.target.style.color = 'var(--color-text-muted)'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Request Reset Step */}
        {step === 'request' && (
          <form onSubmit={handleRequestReset}>
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--color-text-secondary)',
                marginBottom: '6px'
              }}>
                Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  border: '1px solid var(--color-border-strong)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.15s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-warning)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border-strong)'}
                placeholder="Enter your registered email address"
                required
              />
            </div>

            <div style={{
              background: 'var(--color-warning-bg)',
              border: '1px solid #fed7aa',
              borderRadius: '6px',
              padding: '12px',
              marginBottom: '20px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                marginBottom: '4px'
              }}>
                <AlertCircle size={16} color="#d97706" />
                <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--color-warning-fg)' }}>
                  Password Reset Instructions
                </span>
              </div>
              <p style={{
                fontSize: '12px',
                color: 'var(--color-warning-fg)',
                margin: 0,
                lineHeight: '1.4'
              }}>
                We'll send you an email with a secure link to reset your password. 
                Check your inbox and spam folder.
              </p>
            </div>

            {error && (
              <div style={{
                background: 'var(--color-danger-bg)',
                border: '1px solid var(--color-danger-border)',
                color: 'var(--color-danger-hover)',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                {error}
              </div>
            )}

            <Button
              type="submit"
              loading={loading}
              disabled={loading}
              icon={<Send size={16} />}
              style={{
                width: '100%',
                background: 'var(--gradient-warning)',
                color: 'white',
                border: 'none',
                padding: '12px',
                fontSize: '14px',
                fontWeight: '600'
              }}
            >
              {loading ? 'Sending...' : 'Send Reset Link'}
            </Button>
          </form>
        )}

        {/* Email Sent Step */}
        {step === 'sent' && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'var(--gradient-success)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 24px'
            }}>
              <Check size={32} color="white" />
            </div>

            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              color: 'var(--color-text-primary)',
              marginBottom: '12px'
            }}>
              Reset Link Sent!
            </h3>

            <p style={{
              color: 'var(--color-text-muted)',
              fontSize: '14px',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              We've sent a password reset link to <strong>{email}</strong>. 
              Click the link in the email to reset your password.
            </p>

            <div style={{
              background: '#f0f9ff',
              border: '1px solid var(--color-info-border)',
              borderRadius: '6px',
              padding: '16px',
              marginBottom: '24px',
              textAlign: 'left'
            }}>
              <h4 style={{
                fontSize: '14px',
                fontWeight: '600',
                color: 'var(--color-info-fg)',
                margin: '0 0 8px 0'
              }}>
                📧 What to do next:
              </h4>
              <ul style={{
                fontSize: '12px',
                color: 'var(--color-info-fg)',
                margin: 0,
                paddingLeft: '16px',
                lineHeight: '1.4'
              }}>
                <li>Check your email inbox</li>
                <li>Look for an email from our system</li>
                <li>Click the "Reset Password" button in the email</li>
                <li>If you don't see it, check your spam folder</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <Button
                onClick={goBackToRequest}
                variant="secondary"
                icon={<ArrowLeft size={16} />}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '14px'
                }}
              >
                Send to Different Email
              </Button>
              <Button
                onClick={goToResetStep}
                style={{
                  flex: 1,
                  background: 'var(--gradient-info)',
                  color: 'white',
                  border: 'none',
                  padding: '10px',
                  fontSize: '14px'
                }}
              >
                I Have the Code
              </Button>
            </div>
          </div>
        )}

        {/* Reset Password Step */}
        {step === 'reset' && (
          <form onSubmit={handleResetPassword}>
            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--color-text-secondary)',
                marginBottom: '6px'
              }}>
                Reset Code/Token
              </label>
              <input
                type="text"
                value={resetToken}
                onChange={(e) => setResetToken(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-border-strong)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.15s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-info)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border-strong)'}
                placeholder="Enter the code from your email"
                required
              />
            </div>

            <div style={{ marginBottom: '16px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--color-text-secondary)',
                marginBottom: '6px'
              }}>
                New Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-border-strong)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.15s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-info)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border-strong)'}
                placeholder="Enter new password (min 6 characters)"
                required
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: 'var(--color-text-secondary)',
                marginBottom: '6px'
              }}>
                Confirm Password
              </label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid var(--color-border-strong)',
                  borderRadius: '6px',
                  fontSize: '14px',
                  outline: 'none',
                  transition: 'border-color 0.15s ease'
                }}
                onFocus={(e) => e.target.style.borderColor = 'var(--color-info)'}
                onBlur={(e) => e.target.style.borderColor = 'var(--color-border-strong)'}
                placeholder="Confirm your new password"
                required
              />
            </div>

            {error && (
              <div style={{
                background: 'var(--color-danger-bg)',
                border: '1px solid var(--color-danger-border)',
                color: 'var(--color-danger-hover)',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '20px'
              }}>
                {error}
              </div>
            )}

            {success && (
              <div style={{
                background: 'var(--color-success-bg)',
                border: '1px solid var(--color-success-border)',
                color: 'var(--color-success-fg)',
                padding: '12px',
                borderRadius: '6px',
                fontSize: '14px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Check size={16} />
                {success}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px' }}>
              <Button
                type="button"
                onClick={() => setStep('sent')}
                variant="secondary"
                icon={<ArrowLeft size={16} />}
                style={{
                  flex: 1,
                  padding: '10px',
                  fontSize: '14px'
                }}
              >
                Back
              </Button>
              <Button
                type="submit"
                loading={loading}
                disabled={loading}
                style={{
                  flex: 1,
                  background: 'var(--gradient-info)',
                  color: 'white',
                  border: 'none',
                  padding: '10px',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </form>
        )}
      </div>
    </Modal>
  );
};

export default ForgotPasswordModal;